(function (root) {
  'use strict';
  const labels = { ulta: 'Ulta Beauty', nordstrom: 'Nordstrom' };
  function normalizeZip(value) {
    const match = String(value).trim().match(/^(\d{5})(?:-\d{4})?$/);
    return match ? match[1] : null;
  }
  function distanceMiles(a, b) {
    const rad = Math.PI / 180;
    const dLat = (b.lat - a.lat) * rad, dLng = (b.lng - a.lng) * rad;
    const x = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * rad) * Math.cos(b.lat * rad) * Math.sin(dLng / 2) ** 2;
    return 3958.7613 * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(Math.max(0, 1 - x)));
  }
  function findStores(stores, origin, retailer, radius) {
    const ranked = stores.filter(s => (retailer === 'all' || s.retailer === retailer) && Number.isFinite(s.lat) && Number.isFinite(s.lng))
      .map(s => ({ ...s, distance: distanceMiles(origin, s) }))
      .sort((a, b) => a.distance - b.distance || a.id.localeCompare(b.id));
    const nearby = ranked.filter(s => s.distance <= radius);
    return { stores: nearby.length ? nearby : ranked.slice(0, 6), nearby: nearby.length > 0 };
  }
  function retailerUrl(store) {
    try {
      const url = new URL(store.url);
      const host = store.retailer === 'ulta' ? 'www.ulta.com' : 'www.nordstrom.com';
      return url.protocol === 'https:' && url.hostname === host ? url.href : null;
    } catch (_) { return null; }
  }
  const api = { normalizeZip, distanceMiles, findStores, retailerUrl };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (!root.customElements || root.customElements.get('potion-store-finder')) return;
  class PotionStoreFinder extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      const retailer = new URLSearchParams(root.location.search).get('retailer');
      if (retailer === 'ulta' || retailer === 'nordstrom') {
        this.querySelectorAll('.potion-finder__filters input').forEach(input => { input.checked = input.value === retailer; });
      }
      this.form = this.querySelector('form'); this.zipInput = this.form.elements.zip;
      this.submit = this.form.querySelector('button'); this.error = this.querySelector('[data-error]');
      this.status = this.querySelector('[data-status]'); this.resultsTitle = this.querySelector('[data-results-title]');
      this.list = this.querySelector('[data-store-list]'); this.results = this.querySelector('[data-results]');
      this.more = this.querySelector('[data-more]'); this.map = this.querySelector('[data-map]');
      this.mapToggle = this.querySelector('[data-map-toggle]'); this.limit = 6;
      this.form.addEventListener('submit', event => { event.preventDefault(); this.search(); });
      this.querySelectorAll('.potion-finder__filters input').forEach(input => input.addEventListener('change', () => { if (this.origin) this.render(); }));
      this.querySelector('[data-radius]').addEventListener('change', () => { if (this.origin) this.render(); });
      this.more.addEventListener('click', () => { this.limit += 6; this.renderList(); });
      this.mapToggle.addEventListener('click', () => {
        if (!this.selectedStore) return;
        if (!this.map.hidden) this.closeMap(); else this.showMap(this.selectedStore);
      });
    }
    async loadData() {
      if (!this.dataPromise) {
        this.dataPromise = Promise.all([this.dataset.storesUrl, this.dataset.zipsUrl].map(async url => {
          const response = await fetch(url, { credentials: 'omit' });
          if (!response.ok) throw new Error('Directory unavailable');
          return response.json();
        })).then(([directory, zips]) => {
          if (!Array.isArray(directory.stores) || !directory.stores.length || !zips || typeof zips !== 'object') throw new Error('Invalid directory');
          this.stores = directory.stores; this.zips = zips;
          if (/^\d{4}-\d{2}-\d{2}$/.test(directory.updated)) this.querySelector('[data-updated]').textContent = `Directory checked ${new Date(directory.updated + 'T12:00:00Z').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })}.`;
        }).catch(error => { this.dataPromise = null; throw error; });
      }
      return this.dataPromise;
    }
    async search() {
      const zip = normalizeZip(this.zipInput.value);
      this.error.textContent = ''; this.zipInput.removeAttribute('aria-invalid');
      if (!zip) return this.showError('Please enter a valid 5-digit U.S. ZIP code.');
      this.submit.disabled = true; this.setAttribute('aria-busy', 'true'); this.status.textContent = 'Finding your nearest stores…';
      try {
        await this.loadData();
        const location = this.zips[zip];
        if (!location || !Number.isFinite(location[0]) || !Number.isFinite(location[1])) {
          this.status.textContent = this.origin ? `Showing results for ${this.searchedZip}.` : 'Enter your ZIP code to find your nearest retailer.';
          return this.showError('We couldn’t find that U.S. ZIP code. Please check it and try again.');
        }
        this.origin = { lat: location[0], lng: location[1] }; this.searchedZip = zip;
        this.place = `${location[2]}, ${location[3]}`; this.zipInput.value = zip;
        this.render();
      } catch (_) {
        this.status.textContent = 'Store search is temporarily unavailable.';
        this.showError('We couldn’t load the store directory. Please try again, or visit the retailers’ store finders below.');
        this.addFallbackLinks();
      } finally { this.submit.disabled = false; this.removeAttribute('aria-busy'); }
    }
    showError(message) { this.error.textContent = message; this.zipInput.setAttribute('aria-invalid', 'true'); this.zipInput.focus(); }
    addFallbackLinks() {
      this.error.append(document.createTextNode(' '));
      [['Ulta Beauty', 'https://www.ulta.com/stores'], ['Nordstrom', 'https://www.nordstrom.com/stores']].forEach(([name, url]) => {
        const a = document.createElement('a'); a.textContent = `${name} store finder`; a.href = url; a.target = '_blank'; a.rel = 'noopener'; this.error.append(a, document.createTextNode(' '));
      });
    }
    render() {
      const retailer = this.querySelector('.potion-finder__filters input:checked').value;
      const radius = Number(this.querySelector('[data-radius]').value);
      const found = findStores(this.stores, this.origin, retailer, radius);
      this.rankedStores = found.stores; this.limit = 6;
      this.resultsTitle.textContent = found.nearby ? `Your ritual, near ${this.searchedZip}.` : 'A little further, still worth the trip.';
      this.status.textContent = found.nearby ? `${this.rankedStores.length} ${retailer === 'all' ? '' : labels[retailer] + ' '}store${this.rankedStores.length === 1 ? '' : 's'} within ${radius} miles of ${this.place}.` : `No ${retailer === 'all' ? '' : labels[retailer] + ' '}stores within ${radius} miles of ${this.place}. Here are the closest locations.`;
      this.results.hidden = !this.rankedStores.length; this.querySelector('[data-welcome]').hidden = true;
      this.renderList();
      if (this.rankedStores.length) this.selectStore(this.rankedStores[0]);
    }
    renderList() {
      this.list.replaceChildren();
      this.rankedStores.slice(0, this.limit).forEach(store => {
        const item = document.createElement('li'); item.className = 'potion-finder__card';
        const content = document.createElement('div');
        const retailer = document.createElement('p'); retailer.className = 'potion-finder__retailer'; retailer.textContent = labels[store.retailer];
        const name = document.createElement('h3'); name.textContent = store.retailer === 'ulta' ? store.city : store.name.replace(/^Nordstrom\s+/, '');
        const address = document.createElement('address'); address.className = 'potion-finder__address'; address.append(document.createTextNode(store.street), document.createElement('br'), document.createTextNode(`${store.city}, ${store.state} ${store.zip}`));
        content.append(retailer, name, address);
        const distance = document.createElement('span'); distance.className = 'potion-finder__distance'; distance.textContent = `${store.distance < .1 ? '<0.1' : store.distance.toFixed(1)} mi`;
        const links = document.createElement('div'); links.className = 'potion-finder__card-links';
        const directions = document.createElement('a'); directions.textContent = 'Get directions'; directions.href = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${store.street}, ${store.city}, ${store.state} ${store.zip}`)}`; directions.target = '_blank'; directions.rel = 'noopener'; directions.setAttribute('aria-label', `Get directions to ${store.name}, ${store.street} (opens in new tab)`); links.append(directions);
        const url = retailerUrl(store);
        if (url) { const details = document.createElement('a'); details.textContent = 'Store details'; details.href = url; details.target = '_blank'; details.rel = 'noopener'; details.setAttribute('aria-label', `Store details for ${store.name}, ${store.street} (opens in new tab)`); links.append(details); }
        const phone = String(store.phone || '').replace(/[^+\d]/g, '');
        if (/^\+?\d{10,15}$/.test(phone)) { const call = document.createElement('a'); call.href = `tel:${phone}`; call.textContent = 'Call store'; call.setAttribute('aria-label', `Call ${store.name}, ${store.street}`); links.append(call); }
        const mapButton = document.createElement('button'); mapButton.type = 'button'; mapButton.textContent = 'View map'; mapButton.setAttribute('aria-label', `View map for ${store.name}, ${store.street}`); mapButton.addEventListener('click', () => { this.selectStore(store); this.showMap(store); this.mapToggle.focus(); }); links.append(mapButton);
        item.append(content, distance, links); this.list.append(item);
      });
      this.more.hidden = this.limit >= this.rankedStores.length;
      this.more.textContent = `Show more stores (${Math.min(this.limit, this.rankedStores.length)} of ${this.rankedStores.length})`;
    }
    selectStore(store) {
      const wasOpen = !this.map.hidden; this.closeMap(); this.selectedStore = store;
      this.querySelector('[data-map-heading]').textContent = store.name;
      this.querySelector('[data-map-address]').textContent = `${store.street}, ${store.city}, ${store.state} ${store.zip}`;
      if (wasOpen) this.showMap(store);
    }
    closeMap() { this.map.hidden = true; this.map.replaceChildren(); this.mapToggle.setAttribute('aria-expanded', 'false'); this.mapToggle.textContent = 'Show map'; }
    showMap(store) {
      const { lat, lng } = store;
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
      const iframe = document.createElement('iframe'); iframe.title = `Map of ${store.name}, ${store.street}`; iframe.loading = 'lazy'; iframe.referrerPolicy = 'no-referrer';
      const bbox = [lng - .018, lat - .012, lng + .018, lat + .012].join(',');
      iframe.src = `https://www.openstreetmap.org/export/embed.html?bbox=${encodeURIComponent(bbox)}&layer=mapnik&marker=${lat},${lng}`;
      this.map.replaceChildren(iframe); this.map.hidden = false; this.mapToggle.textContent = 'Hide map'; this.mapToggle.setAttribute('aria-expanded', 'true');
    }
  }
  root.customElements.define('potion-store-finder', PotionStoreFinder);
})(typeof window === 'undefined' ? globalThis : window);
